/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useState, useRef, useEffect } from "react";
import swal from "sweetalert";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "admin/components/MDBox";
import MDTypography from "admin/components/MDTypography";
import MDButton from "admin/components/MDButton";
import MDBadge from "admin/components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "admin/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "admin/examples/Navbars/DashboardNavbar";
import DataTable from "admin/examples/Tables/DataTable";

// Data
import eventsTableData from "admin/layouts/events/data/eventsTableData";

// Filter components
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import MDInput from "admin/components/MDInput";

// Modals
import EventModal from "admin/layouts/events/components/EventModal";
import CSVConfirmationModal from "admin/layouts/events/components/CSVConfirmationModal";

// Service
import eventService from "admin/services/eventService";

// CSV Parser
import { parse } from "papaparse";

// Countries list (same as users)
const COUNTRIES_LIST = [
    "Syria",
    "Lebanon",
    "Palestine",
    "Jordan",
    "Iraq",
    "Yemen",
    "Egypt",
    "Saudi Arabia",
    "UAE",
    "Kuwait",
    "Bahrain",
    "Qatar",
    "Oman",
    "Turkey",
    "Iran",
    "Afghanistan",
    "Pakistan",
    "Libya",
    "Tunisia",
    "Algeria",
    "Morocco",
    "Sudan",
    "Somalia",
    "Israel",
];

const SOURCE_OPTIONS = ["Manual Entry", "CSV Imported", "AI (ChatGPT)", "Data Crawling"];
const STATUS_OPTIONS = ["Published", "Unpublished"];

function Events() {
    // Backend data state
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit] = useState(100);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({
        country: null,
        source: null,
        startDate: "",
        endDate: "",
        status: null,
    });

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // CSV import state
    const [csvConfirmOpen, setCsvConfirmOpen] = useState(false);
    const [importedData, setImportedData] = useState([]);
    const [importErrorCount, setImportErrorCount] = useState(0);

    const fileInputRef = useRef(null);

    // Fetch events from backend
    const fetchEvents = async () => {
        try {
            setLoading(true);
            const response = await eventService.getAllEvents(filters, page, limit);
            if (response.status && response.data) {
                setEvents(response.data);
                // Update pagination metadata
                if (response.pagination) {
                    setTotalPages(response.pagination.totalPages);
                    setTotalItems(response.pagination.totalItems);
                    setHasNextPage(response.pagination.hasNextPage);
                    setHasPrevPage(response.pagination.hasPrevPage);
                }
            }
        } catch (error) {
            console.error("Error fetching events:", error);
            swal("Error", error.errorMessage || "Failed to fetch events", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, [page, filters]); // Refetch when page or filters change

    // Handlers for Event modal
    const handleOpenAddModal = () => {
        setSelectedEvent(null);
        setModalOpen(true);
    };

    const handleOpenEditModal = (event) => {
        setSelectedEvent(event);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedEvent(null);
    };

    const handleSaveEvent = async (eventData) => {
        try {
            if (selectedEvent) {
                // Update existing event
                await eventService.updateEvent(selectedEvent._id, eventData);
                swal("Success", "Event updated successfully", "success");
            } else {
                // Create new event
                await eventService.createEvent(eventData);
                swal("Success", "Event created successfully", "success");
            }
            fetchEvents(); // Refresh list
            handleCloseModal();
        } catch (error) {
            console.error("Error saving event:", error);
            swal("Error", error.errorMessage || "Failed to save event", "error");
        }
    };

    const handleDeleteEvent = async (eventId) => {
        swal({
            title: "Are you sure?",
            text: "Once deleted, you will not be able to recover this event!",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                try {
                    await eventService.deleteEvent(eventId);
                    swal("Poof! Event has been deleted!", {
                        icon: "success",
                    });
                    fetchEvents(); // Refresh list
                } catch (error) {
                    console.error("Error deleting event:", error);
                    swal("Error", error.errorMessage || "Failed to delete event", "error");
                }
            }
        });
    };

    // Handle pagination
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    // Handlers for CSV import
    const handleImportCSVClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const csvText = e.target.result;
            const result = parse(csvText, {
                header: true,
                skipEmptyLines: true,
                trimHeaders: true,
                dynamicTyping: true,
            });

            // Basic validation logic (can be expanded)
            const required = ["conflictName", "country", "typeOfViolence"];
            let errors = 0;

            if (result.errors.length) {
                errors += result.errors.length;
            }

            // Check for missing required fields in first row as a sample
            if (result.data.length > 0) {
                const missing = required.filter((field) => !(field in result.data[0]));
                if (missing.length) errors++;
            }

            setImportedData(result.data);
            setImportErrorCount(errors);
            setCsvConfirmOpen(true);

            // Reset input so same file can be selected again if needed
            event.target.value = "";
        };
        reader.readAsText(file);
    };

    const handleConfirmImport = async () => {
        try {
            const response = await eventService.bulkImportEvents(importedData);
            if (response.status) {
                swal(
                    "Success",
                    `Bulk import completed: ${response.data.successful} successful, ${response.data.failed} failed`,
                    "success"
                );
                fetchEvents(); // Refresh list
            }
        } catch (error) {
            console.error("Error importing events:", error);
            swal("Error", error.errorMessage || "Failed to import events", "error");
        } finally {
            setCsvConfirmOpen(false);
            setImportedData([]);
            setImportErrorCount(0);
        }
    };

    const handleCancelImport = () => {
        setCsvConfirmOpen(false);
        setImportedData([]);
        setImportErrorCount(0);
    };

    // Filter logic (client-side)
    const filterEvents = (data) => {
        return data.filter((event) => {
            if (filters.country && event.country !== filters.country) return false;
            if (filters.source && event.typeOfSource !== filters.source) return false;
            if (filters.startDate && event.dateStart < filters.startDate) return false;
            if (filters.endDate && event.dateEnd > filters.endDate) return false;
            if (filters.status && event.status !== filters.status) return false;
            return true;
        });
    };

    const handleClearFilters = () => {
        setFilters({
            country: null,
            source: null,
            startDate: "",
            endDate: "",
            status: null,
        });
        setPage(1); // Reset to first page
    };

    const handleApplyFilters = () => {
        setPage(1); // Reset to first page when applying filters
        fetchEvents();
    };

    // Get table data with backend events
    const filteredEvents = filterEvents(events);
    const { columns, rows } = eventsTableData(filteredEvents, handleOpenEditModal, handleDeleteEvent);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={2} pb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox px={2} pt={1} pb={0.5} display="flex" justifyContent="space-between" alignItems="center">
                                <MDTypography variant="body2" sx={{ color: "#C29717", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1.5px" }}>
                                    Events
                                </MDTypography>
                                <MDBox display="flex" gap={2} alignItems="center">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: "none" }}
                                        accept=".csv"
                                        onChange={handleFileChange}
                                    />
                                    <MDTypography
                                        component="span"
                                        sx={{ color: "#aaa", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }}
                                        onClick={handleImportCSVClick}
                                    >
                                        <Icon sx={{ fontSize: "14px !important" }}>upload_file</Icon> Import CSV
                                    </MDTypography>
                                    <MDTypography
                                        component="span"
                                        sx={{ color: "#aaa", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { color: "#fff" } }}
                                        onClick={handleOpenAddModal}
                                    >
                                        <Icon sx={{ fontSize: "14px !important" }}>add_circle_outline</Icon> Add event
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            {/* Filters Section */}
                            <MDBox px={2} py={1}>
                                
                                <Grid container spacing={1}>
                                    {/* Country Filter */}
                                    <Grid item xs={12} md={2.4}>
                                        <Autocomplete
                                            options={COUNTRIES_LIST}
                                            value={filters.country}
                                            onChange={(event, newValue) => setFilters({ ...filters, country: newValue })}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Country" variant="outlined" size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }} />
                                            )}
                                        />
                                    </Grid>
                                    {/* Source Filter */}
                                    <Grid item xs={12} md={2.4}>
                                        <Autocomplete
                                            options={SOURCE_OPTIONS}
                                            value={filters.source}
                                            onChange={(event, newValue) => setFilters({ ...filters, source: newValue })}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Source" variant="outlined" size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }} />
                                            )}
                                        />
                                    </Grid>
                                    {/* Start Date Filter */}
                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            type="datetime-local"
                                            label="Start Date"
                                            value={filters.startDate}
                                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                                            variant="outlined"
                                            size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    {/* End Date Filter */}
                                    <Grid item xs={12} md={2.4}>
                                        <TextField
                                            type="datetime-local"
                                            label="End Date"
                                            value={filters.endDate}
                                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                                            variant="outlined"
                                            size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }}
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    {/* Status Filter */}
                                    <Grid item xs={12} md={2.4}>
                                        <Autocomplete
                                            options={STATUS_OPTIONS}
                                            value={filters.status}
                                            onChange={(event, newValue) => setFilters({ ...filters, status: newValue })}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Status" variant="outlined" size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }} />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                {/* Clear Filters Button */}
                                <MDBox mt={0.5} display="flex" justifyContent="flex-end">
                                    <MDButton variant="outlined" color="secondary" size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }} onClick={handleClearFilters}>
                                        Clear Filters
                                    </MDButton>
                                </MDBox>
                            </MDBox>

                            {/* Events List */}
                            <MDBox px={2} py={1}>
                                {loading ? (
                                    <MDBox display="flex" justifyContent="center" p={3}>
                                        <div className="forte-spin" style={{margin:"0 auto"}}></div>
                                    </MDBox>
                                ) : filteredEvents.length === 0 ? (
                                    <MDTypography sx={{ color: "#888", fontSize: "13px", py: 2, textAlign: "center" }}>No events</MDTypography>
                                ) : (
                                    filteredEvents.map(ev => (
                                        <MDBox key={ev._id} sx={{ borderBottom: "1px solid rgba(255,255,255,0.04)", py: 1.2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <MDBox sx={{ flex: 1, minWidth: 0 }}>
                                                <MDBox display="flex" alignItems="center" gap={1} mb={0.3}>
                                                    <MDTypography sx={{ color: "#fff", fontSize: "13px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.conflictName || "Untitled"}</MDTypography>
                                                    <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "1px", background: ev.status === "Published" || ev.status === "published" ? "#4CAF50" : "#555", flexShrink: 0 }}></span>
                                                </MDBox>
                                                <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                                    <span style={{ fontSize: "10px", color: "#888" }}>{ev.country || ""}</span>
                                                    {ev.typeOfViolence && <span style={{ fontSize: "9px", color: "#C29717", background: "rgba(194,151,23,0.1)", border: "1px solid rgba(194,151,23,0.15)", borderRadius: "2px", padding: "0 5px" }}>{ev.typeOfViolence}</span>}
                                                    {ev.typeOfSource && <span style={{ fontSize: "9px", color: "#888" }}>{ev.typeOfSource === "Manual Entry" ? "Human Analyst" : ev.typeOfSource}</span>}
                                                    <span style={{ fontSize: "10px", color: "#666" }}>{ev.dateStart ? new Date(ev.dateStart).toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"}) : ""}</span>
                                                </MDBox>
                                                {ev.description && <MDTypography sx={{ color: "#777", fontSize: "11px", mt: 0.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "600px" }}>{ev.description}</MDTypography>}
                                                {(ev.confidenceInfo || ev.confidenceGeo || ev.mediaCount) && (
                                                    <MDBox display="flex" gap={0.5} mt={0.3}>
                                                        {ev.confidenceInfo > 0 && <span style={{fontSize:'9px',color:'#888',background:'rgba(255,255,255,0.04)',borderRadius:'2px',padding:'0 4px'}}>info:{ev.confidenceInfo}/10</span>}
                                                        {ev.confidenceGeo > 0 && <span style={{fontSize:'9px',color:'#888',background:'rgba(255,255,255,0.04)',borderRadius:'2px',padding:'0 4px'}}>geo:{ev.confidenceGeo}/10</span>}
                                                        {ev.mediaCount > 0 && <span style={{fontSize:'9px',color:'#888',background:'rgba(255,255,255,0.04)',borderRadius:'2px',padding:'0 4px'}}>{ev.mediaCount} sources</span>}
                                                        {ev.markers && ev.markers.length > 1 && <span style={{fontSize:'9px',color:'#C29717',background:'rgba(194,151,23,0.08)',borderRadius:'2px',padding:'0 4px'}}>{ev.markers.length} locations</span>}
                                                    </MDBox>
                                                )}
                                            </MDBox>
                                            <MDBox display="flex" gap={0.8} flexShrink={0} ml={1}>
                                                <Icon sx={{ fontSize: "14px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#fff" } }} onClick={() => handleOpenEditModal(ev)}>edit</Icon>
                                                <Icon sx={{ fontSize: "14px !important", color: "#888", cursor: "pointer", "&:hover": { color: "#e55" } }} onClick={() => handleDeleteEvent(ev._id)}>delete</Icon>
                                            </MDBox>
                                        </MDBox>
                                    ))
                                )}
                            </MDBox>

                            {/* Pagination Controls */}
                            {!loading && totalItems > 0 && (
                                <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                                    <MDBox>
                                        <MDTypography variant="caption" color="text">
                                            Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, totalItems)} of {totalItems} events
                                        </MDTypography>
                                        <MDTypography variant="caption" color="text" ml={2}>
                                            Page {page} of {totalPages}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox display="flex" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="secondary"
                                            size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }}
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={!hasPrevPage}
                                        >
                                            <Icon>chevron_left</Icon> Previous
                                        </MDButton>
                                        <MDButton
                                            variant="outlined"
                                            color="secondary"
                                            size="small" sx={{ "& .MuiInputBase-root": { height: "32px", fontSize: "11px" } }}
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={!hasNextPage}
                                        >
                                            Next <Icon>chevron_right</Icon>
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Event Modal */}
            <EventModal open={modalOpen} onClose={handleCloseModal} event={selectedEvent} onSave={handleSaveEvent} />

            {/* CSV Confirmation Modal */}
            <CSVConfirmationModal
                open={csvConfirmOpen}
                onClose={handleCancelImport}
                onConfirm={handleConfirmImport}
                eventCount={importedData.length}
                errorCount={importErrorCount}
            />
        </DashboardLayout>
    );
}

export default Events;
